import * as B from '@blueprintjs/core';
import classnames from 'classnames';
import * as I from 'immutable';
import moment from 'moment';
import React from 'react';

import {ApiOrderableScene} from 'app/modules/Remote/Feature';
import * as featureUtils from 'app/utils/featureUtils';
import {formatCaptureDate, getLayerKeyForScene} from 'app/utils/imageryUtils';

import cs from './styles.module.scss';

const BooleanIcon: React.FC<{value: boolean}> = ({value}) => (
  <B.Icon icon={value ? 'tick' : 'cross'} intent={value ? B.Intent.SUCCESS : B.Intent.DANGER} />
);

/** When a scene was captured, how cloudy it is, and what its source delivers. */
export const SceneDetails: React.FC<{
  scene: I.ImmutableOf<ApiOrderableScene>;
  className?: string;
}> = ({scene, className}) => {
  const {sourceDetails} = featureUtils.imageAndSourceDetailsFromScene(scene);
  const sourceId: string = scene.get('sourceId');
  const layerKey = getLayerKeyForScene(scene);
  const cloudCoverPercentage: number | undefined = scene.get('cloudCoverPercentage');

  const resolution =
    sourceDetails?.resolution != null
      ? parseFloat(sourceDetails.resolution.toFixed(3))
      : sourceDetails?.resolution;

  // Fallback to 'Red, Green, Blue' for some scene bands
  const sceneBands = scene.get('bands');
  const bandDisplay =
    sceneBands != null && sceneBands.size > 0
      ? sceneBands.toArray().join(', ')
      : layerKey.includes('truecolor')
        ? 'Red, Green, Blue'
        : null;

  return (
    <div className={classnames(cs.details, className)}>
      <p>
        This image was taken on{' '}
        <strong>
          {formatCaptureDate(scene.get('sensingTime'), `${sourceId}_high-res-truecolor`, {
            showCaptureTime: true,
            descriptive: true,
          })}
        </strong>
        .
        {cloudCoverPercentage != undefined && (
          <span>
            {' '}
            It is part of a larger scene with <strong>
              {Math.round(cloudCoverPercentage)}%
            </strong>{' '}
            cloud cover.
          </span>
        )}
      </p>
      <B.HTMLTable compact className={cs.table} striped>
        <tbody>
          {sourceDetails && (
            <tr>
              <td className={cs.tableLabel}>Source</td>
              <td>
                {sourceDetails.operator} {sourceDetails.name}
              </td>
            </tr>
          )}
          {resolution != null && (
            <tr>
              <td className={cs.tableLabel}>Resolution</td>
              <td>{resolution}m</td>
            </tr>
          )}
          {bandDisplay && (
            <tr>
              <td className={cs.tableLabel}>Bands</td>
              <td>{bandDisplay}</td>
            </tr>
          )}
          <tr>
            <td className={cs.tableLabel}>Sensing Time (UTC)</td>
            <td>{`${moment(scene.get('sensingTime')).utc().format('YYYY-MM-DDTHH:mm:ss')}Z`}</td>
          </tr>
          {sourceDetails && (
            <>
              <tr>
                <td className={cs.tableLabel}>GeoTIFF download</td>
                <td>
                  <BooleanIcon value={sourceDetails.downloadable} />
                </td>
              </tr>
              <tr>
                <td className={cs.tableLabel}>Share links</td>
                <td>
                  <BooleanIcon value={sourceDetails.shareable} />
                </td>
              </tr>
            </>
          )}
        </tbody>
      </B.HTMLTable>
    </div>
  );
};

/** What to look for in a preview before buying. */
export const ScenePreviewGuidance: React.FC<{isPartialScene: boolean; className?: string}> = ({
  isPartialScene,
  className,
}) => (
  <div className={className}>
    This is a low-resolution preview. It will be high-resolution when processed.
    <br />
    {isPartialScene && (
      <>
        <strong className={cs.uncovered}>Red</strong> areas show where the image does not cover the
        property.{' '}
      </>
    )}
    Please check the preview image for <strong>clouds</strong>, <strong>coloration</strong>,{' '}
    <strong>contrast</strong>, and <strong>coverage area</strong> to ensure that it meets your
    needs.
  </div>
);
